// Makes a workspace bundle valid STIX 2.1 on its way out of the Viewer, whatever made
// it: the PCAP analyser, the sandbox, Import, OSINT lookups or a file someone loaded.
// (Knowledge Base downloads are finished on the server by stix.js, with the same rules.)
//
// Only conformance changes, never content: timestamps in the spec's format, created
// and modified on every SDO/SRO, no nulls or empty lists, properties a type doesn't
// define renamed x_ih_<name> (observables can't carry created, labels or a name),
// valid ids (anything else gets a fresh UUID, and every reference to it follows), no
// spec_version on the bundle itself. Custom x_ih_* and x_mitre_* properties are
// declared through Idle Hour's extension definitions (shared with the server in
// stix-common.json), which travel in the bundle. The Viewer keeps its own copy as it was.

import SHARED from "./stix-common.json";

const EXT_IDS = { x_ih_: SHARED.extensions.idleHour.id, x_mitre_: SHARED.extensions.mitre.id };
const SHARED_BY_ID = new Map([SHARED.idleHour, SHARED.tlpClear, SHARED.extensions.idleHour, SHARED.extensions.mitre, SHARED.extensions.cryptoWallet, SHARED.extensions.cryptoTransaction].map(o => [o.id, o]));

const COMMON = ["type", "spec_version", "id", "created_by_ref", "created", "modified", "revoked", "labels", "confidence", "lang", "external_references", "object_marking_refs", "granular_markings", "extensions"];
const SCO_COMMON = ["type", "spec_version", "id", "object_marking_refs", "granular_markings", "defanged", "extensions"];
const SDO = {
  "attack-pattern": ["name", "description", "aliases", "kill_chain_phases"],
  campaign: ["name", "description", "aliases", "first_seen", "last_seen", "objective"],
  "course-of-action": ["name", "description"],
  grouping: ["name", "description", "context", "object_refs"],
  identity: ["name", "description", "roles", "identity_class", "sectors", "contact_information"],
  incident: ["name", "description", "kill_chain_phases"],
  indicator: ["name", "description", "indicator_types", "pattern", "pattern_type", "pattern_version", "valid_from", "valid_until", "kill_chain_phases"],
  infrastructure: ["name", "description", "infrastructure_types", "aliases", "kill_chain_phases", "first_seen", "last_seen"],
  "intrusion-set": ["name", "description", "aliases", "first_seen", "last_seen", "goals", "resource_level", "primary_motivation", "secondary_motivations"],
  location: ["name", "description", "latitude", "longitude", "precision", "region", "country", "administrative_area", "city", "street_address", "postal_code"],
  malware: ["name", "description", "malware_types", "is_family", "aliases", "kill_chain_phases", "first_seen", "last_seen", "operating_system_refs", "architecture_execution_envs", "implementation_languages", "capabilities", "sample_refs"],
  "malware-analysis": ["product", "version", "host_vm_ref", "operating_system_ref", "installed_software_refs", "configuration_version", "modules", "analysis_engine_version", "analysis_definition_version", "submitted", "analysis_started", "analysis_ended", "result_name", "result", "analysis_sco_refs", "sample_ref"],
  note: ["abstract", "content", "authors", "object_refs"],
  "observed-data": ["first_observed", "last_observed", "number_observed", "objects", "object_refs"],
  opinion: ["explanation", "authors", "opinion", "object_refs"],
  report: ["name", "description", "report_types", "published", "object_refs"],
  "threat-actor": ["name", "description", "threat_actor_types", "aliases", "first_seen", "last_seen", "roles", "goals", "sophistication", "resource_level", "primary_motivation", "secondary_motivations", "personal_motivations"],
  tool: ["name", "description", "tool_types", "aliases", "kill_chain_phases", "tool_version"],
  vulnerability: ["name", "description"],
  relationship: ["relationship_type", "description", "source_ref", "target_ref", "start_time", "stop_time"],
  sighting: ["description", "first_seen", "last_seen", "count", "sighting_of_ref", "observed_data_refs", "where_sighted_refs", "summary"],
};
const SCO = {
  "ipv4-addr": ["value", "resolves_to_refs", "belongs_to_refs"], "ipv6-addr": ["value", "resolves_to_refs", "belongs_to_refs"],
  "domain-name": ["value", "resolves_to_refs"], url: ["value"], "email-addr": ["value", "display_name", "belongs_to_ref"], "mac-addr": ["value"],
  "autonomous-system": ["number", "name", "rir"],
  file: ["hashes", "size", "name", "name_enc", "magic_number_hex", "mime_type", "ctime", "mtime", "atime", "parent_directory_ref", "contains_refs", "content_ref"],
  "network-traffic": ["start", "end", "is_active", "src_ref", "dst_ref", "src_port", "dst_port", "protocols", "src_byte_count", "dst_byte_count", "src_packets", "dst_packets", "ipfix", "src_payload_ref", "dst_payload_ref", "encapsulates_refs", "encapsulated_by_ref"],
  "x509-certificate": ["is_self_signed", "hashes", "version", "serial_number", "signature_algorithm", "issuer", "validity_not_before", "validity_not_after", "subject", "subject_public_key_algorithm", "subject_public_key_modulus", "subject_public_key_exponent", "x509_v3_extensions"],
  software: ["name", "cpe", "swid", "languages", "vendor", "version"], "user-agent": ["value"], directory: ["path", "path_enc", "ctime", "mtime", "atime", "contains_refs"],
  "email-message": ["is_multipart", "date", "content_type", "from_ref", "sender_ref", "to_refs", "cc_refs", "bcc_refs", "message_id", "subject", "received_lines", "additional_header_fields", "body", "body_multipart", "raw_email_ref"],
  process: ["is_hidden", "pid", "created_time", "cwd", "command_line", "environment_variables", "opened_connection_refs", "creator_user_ref", "image_ref", "parent_ref", "child_refs"],
  mutex: ["name"], artifact: ["mime_type", "payload_bin", "url", "hashes", "encryption_algorithm", "decryption_key"],
  // Idle Hour's own observable types (new-sco extensions in stix-common.json).
  "cryptocurrency-wallet": ["value", "blockchain", "tags"],
  "cryptocurrency-transaction": ["value", "blockchain", "timestamp", "block_height", "fee", "input_refs", "output_refs", "inputs", "outputs", "inputs_not_shown", "outputs_not_shown"],
  "windows-registry-key": ["key", "values", "modified_time", "creator_user_ref", "number_of_subkeys"], "user-account": ["user_id", "credential", "account_login", "account_type", "display_name", "is_service_account", "is_privileged", "can_escalate_privs", "is_disabled", "account_created", "account_expires", "credential_last_changed", "account_first_login", "account_last_login"],
};
const TIMESTAMPS = ["created", "modified", "valid_from", "valid_until", "first_seen", "last_seen", "published", "first_observed", "last_observed", "start_time", "stop_time", "submitted", "analysis_started", "analysis_ended"];
const ID_RE = /^[a-z][a-z0-9-]+--[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const newUuid = () => (crypto.randomUUID ? crypto.randomUUID() : "10000000-1000-4000-8000-100000000000".replace(/[018]/g, c => (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)));

// ─── Deterministic observable ids (STIX 2.1 §2.9): UUIDv5 in the STIX namespace over
// the canonical JSON of the type's id-contributing properties. The browser has no
// synchronous SHA-1, so here is a small one.

const SCO_NAMESPACE = "00abedb4-aa42-466c-9c01-fed23315a9b7";

function sha1(bytes) {
  const padded = new Uint8Array(((bytes.length + 9 + 63) >> 6) << 6);
  padded.set(bytes);
  padded[bytes.length] = 0x80;
  const dv = new DataView(padded.buffer);
  const bits = bytes.length * 8;
  dv.setUint32(padded.length - 8, Math.floor(bits / 2 ** 32));
  dv.setUint32(padded.length - 4, bits >>> 0);
  const h = [0x67452301, 0xefcdab89, 0x98badcfe, 0x10325476, 0xc3d2e1f0];
  const w = new Uint32Array(80);
  for (let i = 0; i < padded.length; i += 64) {
    for (let j = 0; j < 16; j++) w[j] = dv.getUint32(i + j * 4);
    for (let j = 16; j < 80; j++) { const x = w[j - 3] ^ w[j - 8] ^ w[j - 14] ^ w[j - 16]; w[j] = (x << 1) | (x >>> 31); }
    let [a, b, c, d, e] = h;
    for (let j = 0; j < 80; j++) {
      const f = j < 20 ? (b & c) | (~b & d) : j < 40 ? b ^ c ^ d : j < 60 ? (b & c) | (b & d) | (c & d) : b ^ c ^ d;
      const k = j < 20 ? 0x5a827999 : j < 40 ? 0x6ed9eba1 : j < 60 ? 0x8f1bbcdc : 0xca62c1d6;
      const tmp = (((a << 5) | (a >>> 27)) + f + e + k + w[j]) >>> 0;
      e = d; d = c; c = ((b << 30) | (b >>> 2)) >>> 0; b = a; a = tmp;
    }
    h[0] = (h[0] + a) >>> 0; h[1] = (h[1] + b) >>> 0; h[2] = (h[2] + c) >>> 0; h[3] = (h[3] + d) >>> 0; h[4] = (h[4] + e) >>> 0;
  }
  const out = new Uint8Array(20);
  const odv = new DataView(out.buffer);
  h.forEach((x, i) => odv.setUint32(i * 4, x));
  return out;
}

export function uuidv5(namespace, name) {
  const ns = Uint8Array.from(namespace.replace(/-/g, "").match(/../g).map(x => parseInt(x, 16)));
  const nm = new TextEncoder().encode(name);
  const buf = new Uint8Array(ns.length + nm.length);
  buf.set(ns); buf.set(nm, ns.length);
  const b = sha1(buf).slice(0, 16);
  b[6] = (b[6] & 0x0f) | 0x50;
  b[8] = (b[8] & 0x3f) | 0x80;
  const hex = [...b].map(x => x.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

// JSON with sorted keys and no whitespace (RFC 8785 for the values STIX uses here).
const canonical = (v) => (Array.isArray(v) ? `[${v.map(canonical).join(",")}]`
  : v && typeof v === "object" ? `{${Object.keys(v).sort().map(k => `${JSON.stringify(k)}:${canonical(v[k])}`).join(",")}}` : JSON.stringify(v));

const HASH_ORDER = ["MD5", "SHA-1", "SHA-256", "SHA-512", "SHA3-256", "SHA3-512", "SSDEEP", "TLSH"];
const oneHash = (hashes) => { const alg = HASH_ORDER.find(a => hashes?.[a]) || Object.keys(hashes || {})[0]; return alg ? { [alg]: hashes[alg] } : undefined; };
const ID_PROPS = {
  "ipv4-addr": ["value"], "ipv6-addr": ["value"], "domain-name": ["value"], url: ["value"], "email-addr": ["value"], "mac-addr": ["value"],
  "autonomous-system": ["number"], mutex: ["name"], directory: ["path"], "user-agent": ["value"],
  file: ["hashes", "name", "extensions", "parent_directory_ref"], artifact: ["hashes", "payload_bin"],
  software: ["name", "cpe", "swid", "vendor", "version"], "x509-certificate": ["hashes", "serial_number"],
  "network-traffic": ["start", "end", "src_ref", "dst_ref", "src_port", "dst_port", "protocols", "extensions"],
  "email-message": ["from_ref", "subject", "body"], "user-account": ["account_type", "user_id", "account_login"], "windows-registry-key": ["key", "values"],
  "cryptocurrency-wallet": ["blockchain", "value"], "cryptocurrency-transaction": ["blockchain", "value"],
};

// The spec's id for an observable, or null when it has none of its contributing properties.
function scoId(o) {
  const props = ID_PROPS[o.type];
  if (!props) return null;
  const c = {};
  for (const p of props) {
    if (o[p] === undefined || o[p] === null || o[p] === "") continue;
    c[p] = p === "hashes" ? oneHash(o[p]) : o[p];
    if (c[p] === undefined) delete c[p];
  }
  return Object.keys(c).length ? `${o.type}--${uuidv5(SCO_NAMESPACE, canonical(c))}` : null;
}

function timestamp(v) {
  const t = Date.parse(String(v ?? ""));
  return Number.isNaN(t) ? null : new Date(t).toISOString();
}

function prune(v) {
  if (Array.isArray(v)) { const a = v.map(prune).filter(x => x !== undefined); return a.length ? a : undefined; }
  if (v && typeof v === "object") {
    const o = {};
    for (const [k, x] of Object.entries(v)) { const p = prune(x); if (p !== undefined) o[k] = p; }
    return Object.keys(o).length ? o : undefined;
  }
  return v === null || v === undefined || v === "" ? undefined : v;
}

// Every *_ref / *_refs value, rewritten through `map`.
function remapRefs(v, map, key = "") {
  if (Array.isArray(v)) return v.map(x => remapRefs(x, map, key));
  if (v && typeof v === "object") return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, remapRefs(x, map, k)]));
  if (typeof v === "string" && /_refs?$/.test(key) && map.has(v)) return map.get(v);
  return v;
}

function finishObject(o, now) {
  const isSco = !!SCO[o.type];
  // Fields the Viewer adds for itself (_ghost, __pinned…) aren't part of the object.
  const out = { type: o.type, spec_version: "2.1", id: o.id };
  for (const [k, v] of Object.entries(prune(o) || {})) if (!k.startsWith("_") && !(k in out)) out[k] = v;
  out.spec_version = "2.1";
  for (const k of TIMESTAMPS) if (k in out) { const t = timestamp(out[k]); if (t) out[k] = t; else delete out[k]; }
  if (isSco) { delete out.created; delete out.modified; }
  const allowed = SDO[out.type] ? new Set([...COMMON, ...SDO[out.type]]) : isSco ? new Set([...SCO_COMMON, ...SCO[out.type]]) : null;
  if (allowed) for (const k of Object.keys(out)) if (!allowed.has(k) && !k.startsWith("x_")) { out[`x_ih_${k}`] = out[k]; delete out[k]; }
  // Custom top-level properties, declared (toplevel-property-extension).
  for (const [prefix, extId] of Object.entries(EXT_IDS)) {
    if (Object.keys(out).some(k => k.startsWith(prefix))) out.extensions = { ...(out.extensions || {}), [extId]: { extension_type: "toplevel-property-extension" } };
  }
  if (isSco || out.type === "marking-definition" || out.type?.startsWith("x-")) return out;
  if (!out.created) out.created = out.modified || out.published || out.first_seen || out.valid_from || now;
  if (!out.modified || out.modified < out.created) out.modified = out.created;
  if (out.type === "indicator") {
    if (!out.pattern_type) out.pattern_type = "stix";
    if (!out.valid_from) out.valid_from = out.created;
  }
  if (out.type === "malware" && out.is_family === undefined) out.is_family = false;
  return out;
}

/** A bundle (or a list of objects) as valid STIX 2.1. */
export function finishBundle(bundleOrObjects) {
  const objects = Array.isArray(bundleOrObjects) ? bundleOrObjects : bundleOrObjects?.objects || [];
  const now = new Date().toISOString();
  const map = new Map();
  for (const o of objects) if (o?.id && o?.type && !ID_RE.test(o.id)) map.set(o.id, `${o.type}--${newUuid()}`);
  // Observables get the spec's deterministic ids: those without references first, then
  // the ones whose ids depend on references (network traffic, email messages).
  for (const pass of [false, true]) {
    for (const o of objects) {
      if (!o?.id || !SCO[o.type] || (ID_PROPS[o.type] || []).some(p => p.endsWith("_ref")) !== pass) continue;
      const id = scoId(remapRefs(o, map));
      if (id && id !== o.id) map.set(o.id, id);
    }
  }
  const seen = new Set();
  const out = [];
  for (const raw of objects) {
    if (!raw?.type || !raw?.id) continue;
    const o = remapRefs({ ...raw, id: map.get(raw.id) || raw.id }, map);
    if (seen.has(o.id)) continue;
    seen.add(o.id);
    out.push(finishObject(o, now));
  }
  // The extension definitions used, and the identity and marking they refer to.
  for (let i = 0; i < out.length; i++) {
    const o = out[i];
    for (const ref of [o.created_by_ref, ...(o.object_marking_refs || []), ...Object.keys(o.extensions || {})]) {
      if (ref && SHARED_BY_ID.has(ref) && !seen.has(ref)) { seen.add(ref); out.push(SHARED_BY_ID.get(ref)); }
    }
  }
  const id = bundleOrObjects?.id && ID_RE.test(bundleOrObjects.id) ? bundleOrObjects.id : `bundle--${newUuid()}`;
  return out.length ? { type: "bundle", id, objects: out } : { type: "bundle", id };
}
